import React from 'react';
import { ImageIcon, ListChecksIcon, AlertTriangleIcon } from 'lucide-react';
import { QualityReport } from '../../types/quality';
import { getStats, photosForPoint } from '../../utils/quality';
import { ProgressRing } from './ProgressRing';
interface ReportPreviewProps {
  report: QualityReport;
}
export function ReportPreview({
  report
}: ReportPreviewProps) {
  const stats = getStats(report);
  const cover = report.photos[0];
  return <article className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm" data-id="element-791" data-mp="25v394py7pdnj">
      {/* Dark hero */}
      <header className="relative isolate overflow-hidden bg-stone-900 px-6 py-8 sm:px-10 sm:py-10" data-id="element-792" data-mp="20ws20mzaxh90">
        {cover && <img src={cover.url} alt="" aria-hidden="true" className="absolute inset-0 -z-10 h-full w-full object-cover opacity-25" data-id="element-793" data-mp="1ru0o8giye9e7" />}
        <div className="absolute inset-0 -z-10 bg-stone-900/70" data-id="element-794" data-mp="3m81nh2if0yet" />

        <div className="flex flex-wrap items-start justify-between gap-4" data-id="element-795" data-mp="3m5nqgo46dc4m">
          <p className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/60" data-id="element-796" data-mp="205jvewowrooj">
            <ListChecksIcon className="h-3.5 w-3.5" data-id="element-797" data-mp="2544zj77a6si9" />
            Kvalitetssikringsrapport
          </p>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white" data-id="element-798" data-mp="1r8t1oqxtwlx4">
            <span className={`h-1.5 w-1.5 rounded-full ${report.status === 'Åben' ? 'bg-emerald-400' : 'bg-white/50'}`} data-id="element-799" data-mp="2ix4bx0bro6ll" />
            {report.status}
          </span>
        </div>

        <div className="mt-5 flex flex-wrap items-end justify-between gap-6" data-id="element-800" data-mp="2a4sx6bq0m7lg">
          <div className="min-w-0" data-id="element-801" data-mp="2e5unu45v9bbt">
            <h2 className="text-2xl font-bold tracking-tight text-white sm:text-3xl" data-id="element-802" data-mp="2e4tb7v97cgrz">
              {report.title}
            </h2>
            <p className="mt-2 text-sm text-white/70" data-id="element-803" data-mp="36mynlekcnp49">{report.customer.name}</p>
          </div>
          <div className="flex items-center gap-3" data-id="element-804" data-mp="3gj79nwas6goa">
            <ProgressRing percent={stats.percent} size={60} onDark data-id="element-805" data-mp="3gino85lx9a5i" />
            {stats.missingPhoto > 0 && <div data-id="element-806" data-mp="3cidgooiheboj">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-white/50" data-id="element-807" data-mp="3cfr94ovlbw6b">
                  Dokumentation
                </p>
                <p className="text-sm font-semibold text-white" data-id="element-808" data-mp="32icyx709g3mg">
                  {stats.missingPhoto} mangler
                </p>
              </div>}
          </div>
        </div>

        {/* Contacts */}
        <div className="mt-8 grid grid-cols-1 gap-6 border-t border-white/10 pt-6 sm:grid-cols-2" data-id="element-809" data-mp="2ss1uljcnzsf1">
          {[{
          label: 'Ansvarlig',
          name: report.responsible.name,
          lines: [report.responsible.email, report.responsible.phone]
        }, {
          label: 'Virksomhed',
          name: report.company.name,
          lines: [report.company.email, report.company.website]
        }].map(block => <div key={block.label} className="min-w-0" data-id="element-810" data-mp="1zqai7d4xtvzh">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-white/50" data-id="element-811" data-mp="24pdikk9z3e14">
                {block.label}
              </p>
              <p className="mt-1.5 truncate text-sm font-semibold text-white" data-id="element-812" data-mp="29priff1f7ho2">
                {block.name}
              </p>
              {block.lines.map(line => <p key={line} className="truncate text-sm text-white/60" data-id="element-813" data-mp="1h1dkt0e8und1">
                  {line}
                </p>)}
            </div>)}
        </div>

        {/* Stats */}
        <dl className="mt-8 grid grid-cols-3 gap-4 border-t border-white/10 pt-6" data-id="element-814" data-mp="1qxgv4tj91sb2">
          {[{
          label: 'Kontrolpunkter',
          value: `${stats.completedPoints}/${stats.totalPoints}`,
          icon: ListChecksIcon
        }, {
          label: 'Fotos',
          value: report.photos.length,
          icon: ImageIcon
        }, {
          label: 'Afvigelser',
          value: stats.deviations,
          icon: AlertTriangleIcon
        }].map(item => {
          const Icon = item.icon;
          return <div key={item.label} data-id="element-815" data-mp="3ep7plj5z3041">
                <dd className="flex items-center gap-2 text-xl font-bold text-white" data-id="element-816" data-mp="39xg2jim0zcin">
                  <Icon className="h-4 w-4 text-white/40" data-id="element-817" data-mp="3nokwq56mjcll" />
                  {item.value}
                </dd>
                <dt className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-white/50" data-id="element-818" data-mp="3jn9dqii99pmd">
                  {item.label}
                </dt>
              </div>;
        })}
        </dl>
      </header>

      {/* Body */}
      <div className="space-y-8 px-6 py-8 sm:px-10" data-id="element-819" data-mp="3so7bwk60j2x1">
        <section data-id="element-820" data-mp="238hq0iw216je">
          <h3 className="text-[11px] font-semibold uppercase tracking-wider text-stone-400" data-id="element-821" data-mp="237y6wyuo4c3s">
            Beskrivelse
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-stone-700" data-id="element-822" data-mp="1ybhg1ww33jn0">
            {report.description}
          </p>
        </section>

        {report.sections.map(section => <section key={section.id} data-id="element-823" data-mp="ipdv1awqu0sp">
            <h3 className="text-[11px] font-semibold uppercase tracking-wider text-stone-400" data-id="element-824" data-mp="isjkir9ab4oy">
              {section.kind}
            </h3>
            <h4 className="mt-1 text-base font-semibold text-stone-900" data-id="element-825" data-mp="90407naim40w">
              {section.title}
            </h4>
            <ul className="mt-3 divide-y divide-stone-100 border-t border-stone-100" data-id="element-826" data-mp="3r5ph5czmn2uc">
              {section.points.map(point => {
            const count = photosForPoint(report, point.id).length;
            return <li key={point.id} className="flex items-center gap-3 py-2.5 text-sm" data-id="element-827" data-mp="3hafaud2749ar">
                    <span className={`h-2 w-2 flex-shrink-0 rounded-full ${point.status === 'ok' ? 'bg-emerald-500' : point.status === 'deviation' ? 'bg-red-500' : 'bg-stone-300'}`} data-id="element-828" data-mp="1fgprhktoi96e" />
                    <span className="min-w-0 flex-1 truncate text-stone-800" data-id="element-829" data-mp="19064p37w2zg3">
                      {point.title}
                    </span>
                    <span className="flex-shrink-0 text-xs text-stone-400" data-id="element-830" data-mp="150p0ed4788ci">
                      {count > 0 ? `${count} foto` : 'ingen foto'}
                    </span>
                    <span className={`w-20 flex-shrink-0 text-right text-xs font-semibold ${point.status === 'ok' ? 'text-emerald-600' : point.status === 'deviation' ? 'text-red-600' : 'text-stone-400'}`} data-id="element-831" data-mp="1wq0ibn018s1e">
                      {point.status === 'ok' ? 'Godkendt' : point.status === 'deviation' ? 'Afvigelse' : 'Afventer'}
                    </span>
                  </li>;
          })}
            </ul>
          </section>)}

        {report.photos.length > 0 && <section data-id="element-832" data-mp="g6sy2fy817yt">
            <h3 className="text-[11px] font-semibold uppercase tracking-wider text-stone-400" data-id="element-833" data-mp="g7chy27fm6fh">
              Dokumentation på arbejdet
            </h3>
            <ul className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3" data-id="element-834" data-mp="q4h3bztgdxv8">
              {report.photos.map(photo => <li key={photo.id} data-id="element-835" data-mp="uunqdv8bhabd">
                  <span className="block aspect-[4/3] overflow-hidden rounded-lg bg-stone-100" data-id="element-836" data-mp="uvpdwz6irp9z">
                    <img src={photo.url} alt={photo.description || 'Dokumentationsfoto'} className="h-full w-full object-cover" data-id="element-837" data-mp="yvhgmv98f17f" />
                  </span>
                  {photo.description && <p className="mt-1.5 truncate text-xs text-stone-500" data-id="element-838" data-mp="jqqhg45gz4uv">
                      {photo.description}
                    </p>}
                </li>)}
            </ul>
          </section>}
      </div>
    </article>;
}